import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle,
  Circle,
  Upload,
  Info,
  Palette,
  Globe,
  X,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Star,
  Users,
  Zap,
  Building,
  Mail,
  Phone,
  Save,
  Check,
  AlertCircle
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import { useBranding } from '../../context/BrandingContext'

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const { cmsName } = useBranding()
  const [currentStep, setCurrentStep] = useState(0)
  const [isStepByStep, setIsStepByStep] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    domain: '',
    logo: null,
    brand_colors: ['#3B82F6', '#1E40AF']
  })

  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [errors, setErrors] = useState({})
  const [stepCompletions, setStepCompletions] = useState({})

  const { data: checklist, isLoading, refetch } = useQuery(
    'onboarding-checklist',
    () => apiService.get('/clients/onboarding/checklist').then(res => res.data),
    {
      refetchOnWindowFocus: false,
      enabled: isOpen,
      refetchInterval: isOpen ? 30000 : false,
    }
  )

  const { data: profile } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data),
    {
      enabled: isOpen,
      onSuccess: (data) => {
        setFormData(prev => ({
          ...prev,
          company_name: data.company_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          description: data.description || '',
          domain: data.domain || '',
          brand_colors: data.brand_colors || prev.brand_colors
        }))
      }
    }
  )

  const updateProfileMutation = useMutation(
    async (profileData) => {
      const response = await apiService.put('/clients/profile', profileData)

      await apiService.post('/clients/onboarding/update', {
        itemId: 'about',
        completed: true
      })

      if (profileData.domain && profileData.domain.trim()) {
        await apiService.post('/clients/onboarding/update', {
          itemId: 'domain',
          completed: true
        })
      }

      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        setStepCompletions(prev => ({ ...prev, about: true, domain: formData.domain ? true : false }))
      },
      onError: (error) => {
        setErrors(prev => ({ ...prev, profile: error.response?.data?.error || 'Failed to save profile' }))
      }
    }
  )

  const uploadLogoMutation = useMutation(
    async (logoFile) => {
      const formDataUpload = new FormData()
      formDataUpload.append('logo', logoFile)

      const response = await apiService.post('/clients/logo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      await apiService.post('/clients/onboarding/update', {
        itemId: 'logo',
        completed: true
      })

      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        setStepCompletions(prev => ({ ...prev, logo: true }))
        setErrors(prev => ({ ...prev, logo: null }))
      },
      onError: (error) => {
        setErrors(prev => ({ ...prev, logo: error.response?.data?.error || 'Failed to upload logo' }))
      }
    }
  )

  const updateBrandColorsMutation = useMutation(
    async (colors) => {
      const response = await apiService.put('/clients/profile', { brand_colors: colors })

      await apiService.post('/clients/onboarding/update', {
        itemId: 'brand_color',
        completed: true
      })

      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        setStepCompletions(prev => ({ ...prev, brand_color: true }))
        setErrors(prev => ({ ...prev, brand_color: null }))
      },
      onError: (error) => {
        setErrors(prev => ({ ...prev, brand_color: error.response?.data?.error || 'Failed to save brand colors' }))
      }
    }
  )

  const completeOnboardingMutation = useMutation(
    () => apiService.post('/clients/onboarding/complete'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        queryClient.invalidateQueries('client-profile')
        if (onComplete) onComplete()
        if (onClose) onClose()
      }
    }
  )

  const handleSkipForNow = () => {
    if (onClose) onClose()
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleLogoUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }))
      uploadLogoMutation.mutate(file)
    }
  }

  const handleColorChange = (index, color) => {
    const newColors = [...formData.brand_colors]
    newColors[index] = color
    setFormData(prev => ({ ...prev, brand_colors: newColors }))
  }

  const handleSaveProfile = () => {
    const profileData = {
      company_name: formData.company_name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      description: formData.description,
      domain: formData.domain
    }
    updateProfileMutation.mutate(profileData)
  }

  const handleSaveBrandColors = () => {
    updateBrandColorsMutation.mutate(formData.brand_colors)
  }

  const getStepByStepFlow = () => {
    if (!checklist) return []

    return [
      {
        id: 'welcome',
        title: `Welcome to ${cmsName}!`,
        description: 'Let\'s get you set up in just a few steps',
        icon: Sparkles,
        isAction: false,
        content: (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ width: '5rem', height: '5rem', background: 'var(--accent-soft)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <Star style={{ width: '2.5rem', height: '2.5rem', color: 'var(--accent)' }} />
            </div>
            <h3 className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '1rem' }}>
              Welcome to {cmsName}!
            </h3>
            <p className="text-soft" style={{ marginBottom: '1.5rem', maxWidth: '28rem', margin: '0 auto 1.5rem' }}>
              We're excited to help you create amazing websites. Let's get your account set up with these quick steps.
            </p>
            <div className="flex items-center justify-center gap-6 text-faint" style={{ fontSize: '0.875rem' }}>
              <div className="flex items-center gap-2">
                <Users style={{ width: '1rem', height: '1rem' }} />
                <span>Trusted by 1000+ clients</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap style={{ width: '1rem', height: '1rem' }} />
                <span>Setup in 5 minutes</span>
              </div>
            </div>
          </div>
        )
      },
      {
        id: 'logo',
        title: 'Upload Your Logo',
        description: 'Add your company logo to personalize your websites',
        icon: Upload,
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'logo')?.completed || stepCompletions.logo,
        required: true,
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '5rem', height: '5rem', background: 'var(--accent-soft)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                <Upload style={{ width: '2.5rem', height: '2.5rem', color: 'var(--accent)' }} />
              </div>
              <h3 className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '1rem' }}>
                Upload Your Logo
              </h3>
              <p className="text-soft" style={{ marginBottom: '2rem', maxWidth: '28rem', margin: '0 auto 2rem' }}>
                Add your company logo to personalize your websites and create a professional brand presence.
              </p>
            </div>

            <div style={{ maxWidth: '28rem', margin: '0 auto' }}>
              <div style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius)', padding: '2rem', textAlign: 'center', transition: 'border-color 0.2s' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                  id="logo-upload"
                />
                <label htmlFor="logo-upload" style={{ cursor: 'pointer' }}>
                  <Upload style={{ width: '3rem', height: '3rem', color: 'var(--text-faint)', margin: '0 auto 1rem', display: 'block' }} />
                  <p className="text-soft" style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Click to upload or drag and drop
                  </p>
                  <p className="text-faint" style={{ fontSize: '0.75rem' }}>
                    PNG, JPG, GIF up to 10MB
                  </p>
                </label>
              </div>

              {formData.logo && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--success-soft)', border: '1px solid var(--success)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="flex items-center gap-2">
                    <Check style={{ width: '1rem', height: '1rem', color: 'var(--success)' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--success)' }}>
                      {formData.logo.name} selected
                    </span>
                  </div>
                </div>
              )}

              {uploadLogoMutation.isLoading && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="flex items-center gap-2">
                    <div style={{ width: '1rem', height: '1rem', border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--accent)' }}>
                      Uploading logo...
                    </span>
                  </div>
                </div>
              )}

              {errors.logo && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--danger-soft)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="flex items-center gap-2">
                    <AlertCircle style={{ width: '1rem', height: '1rem', color: 'var(--danger)' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--danger)' }}>
                      {errors.logo}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'about',
        title: 'Complete Your Profile',
        description: 'Fill in your company information and details',
        icon: Info,
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'about')?.completed || stepCompletions.about,
        required: true,
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '5rem', height: '5rem', background: 'var(--success-soft)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                <Building style={{ width: '2.5rem', height: '2.5rem', color: 'var(--success)' }} />
              </div>
              <h3 className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '1rem' }}>
                Complete Your Profile
              </h3>
              <p className="text-soft" style={{ maxWidth: '28rem', margin: '0 auto 2rem' }}>
                Tell us about your company so we can personalize your experience.
              </p>
            </div>

            <div style={{ maxWidth: '28rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
              <div>
                <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                  Company Name *
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  className="input w-full"
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: 'var(--text-faint)' }} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input w-full"
                    style={{ paddingLeft: '2.25rem' }}
                    placeholder="company@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                  Phone Number
                </label>
                <div style={{ position: 'relative' }}>
                  <Phone style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: 'var(--text-faint)' }} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="input w-full"
                    style={{ paddingLeft: '2.25rem' }}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                  Company Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="input w-full"
                  style={{ resize: 'vertical' }}
                  placeholder="Tell us about your company..."
                />
              </div>

              <button
                className="btn btn-primary w-full"
                onClick={handleSaveProfile}
                disabled={updateProfileMutation.isLoading || !formData.company_name.trim()}
              >
                <Save style={{ width: '1rem', height: '1rem' }} />
                {updateProfileMutation.isLoading ? 'Saving...' : 'Save Profile'}
              </button>

              {updateProfileMutation.isSuccess && (
                <div style={{ padding: '0.75rem', background: 'var(--success-soft)', border: '1px solid var(--success)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="flex items-center gap-2">
                    <Check style={{ width: '1rem', height: '1rem', color: 'var(--success)' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--success)' }}>
                      Profile saved successfully!
                    </span>
                  </div>
                </div>
              )}

              {errors.profile && (
                <div style={{ padding: '0.75rem', background: 'var(--danger-soft)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="flex items-center gap-2">
                    <AlertCircle style={{ width: '1rem', height: '1rem', color: 'var(--danger)' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--danger)' }}>
                      {errors.profile}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'brand_color',
        title: 'Set Brand Colors',
        description: 'Choose colors that represent your brand',
        icon: Palette,
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'brand_color')?.completed || stepCompletions.brand_color,
        required: true,
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '5rem', height: '5rem', background: 'var(--accent-soft)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                <Palette style={{ width: '2.5rem', height: '2.5rem', color: 'var(--accent)' }} />
              </div>
              <h3 className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '1rem' }}>
                Set Brand Colors
              </h3>
              <p className="text-soft" style={{ maxWidth: '28rem', margin: '0 auto 2rem' }}>
                Choose colors that represent your brand. These will be used throughout your websites.
              </p>
            </div>

            <div style={{ maxWidth: '28rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.brand_colors[0]}
                      onChange={(e) => handleColorChange(0, e.target.value)}
                      style={{ width: '3rem', height: '3rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.brand_colors[0]}
                      onChange={(e) => handleColorChange(0, e.target.value)}
                      className="input"
                      style={{ flex: 1 }}
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.brand_colors[1]}
                      onChange={(e) => handleColorChange(1, e.target.value)}
                      style={{ width: '3rem', height: '3rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.brand_colors[1]}
                      onChange={(e) => handleColorChange(1, e.target.value)}
                      className="input"
                      style={{ flex: 1 }}
                      placeholder="#1E40AF"
                    />
                  </div>
                </div>
              </div>

              <div style={{ padding: '1rem', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
                <h4 className="font-medium" style={{ fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>Preview</h4>
                <div className="flex gap-2">
                  <div
                    style={{ width: '4rem', height: '4rem', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-sm)', backgroundColor: formData.brand_colors[0] }}
                  />
                  <div
                    style={{ width: '4rem', height: '4rem', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-sm)', backgroundColor: formData.brand_colors[1] }}
                  />
                </div>
              </div>

              <button
                className="btn btn-primary w-full"
                onClick={handleSaveBrandColors}
                disabled={updateBrandColorsMutation.isLoading}
              >
                <Palette style={{ width: '1rem', height: '1rem' }} />
                {updateBrandColorsMutation.isLoading ? 'Saving...' : 'Save Brand Colors'}
              </button>

              {updateBrandColorsMutation.isSuccess && (
                <div style={{ padding: '0.75rem', background: 'var(--success-soft)', border: '1px solid var(--success)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="flex items-center gap-2">
                    <Check style={{ width: '1rem', height: '1rem', color: 'var(--success)' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--success)' }}>
                      Brand colors saved successfully!
                    </span>
                  </div>
                </div>
              )}

              {errors.brand_color && (
                <div style={{ padding: '0.75rem', background: 'var(--danger-soft)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="flex items-center gap-2">
                    <AlertCircle style={{ width: '1rem', height: '1rem', color: 'var(--danger)' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--danger)' }}>
                      {errors.brand_color}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'domain',
        title: 'Your Website URL',
        description: 'Your site is already live on a subdomain — upgrade to a custom domain anytime',
        icon: Globe,
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'domain')?.completed || stepCompletions.domain,
        required: false,
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '5rem', height: '5rem', background: 'var(--accent-soft)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                <Globe style={{ width: '2.5rem', height: '2.5rem', color: 'var(--accent)' }} />
              </div>
              <h3 className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '1rem' }}>
                Your Website URL
              </h3>
              <p className="text-soft" style={{ maxWidth: '28rem', margin: '0 auto 2rem' }}>
                Your site is already accessible on a subdomain. You can connect your own domain anytime from the Domain settings.
              </p>
            </div>

            <div style={{ maxWidth: '28rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
              {profile?.assigned_subdomain && (
                <div style={{ padding: '1rem', background: 'var(--success-soft)', border: '1px solid var(--success)', borderRadius: 'var(--radius)' }}>
                  <p className="font-medium" style={{ fontSize: '0.75rem', color: 'var(--success)', marginBottom: '0.25rem' }}>Your subdomain (ready now)</p>
                  <div className="flex items-center justify-between gap-2">
                    <code style={{ fontSize: '0.875rem', fontFamily: 'monospace', color: 'var(--success)', wordBreak: 'break-all' }}>
                      https://{profile.assigned_subdomain}
                    </code>
                    <a
                      href={`https://${profile.assigned_subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ flexShrink: 0, color: 'var(--success)' }}
                    >
                      <Globe style={{ width: '1rem', height: '1rem' }} />
                    </a>
                  </div>
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                <p className="font-medium" style={{ fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.75rem' }}>
                  Have your own domain? (optional)
                </p>
                <div style={{ position: 'relative' }}>
                  <Globe style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: 'var(--text-faint)' }} />
                  <input
                    type="url"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    className="input w-full"
                    style={{ paddingLeft: '2.25rem' }}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                <p className="text-faint" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Full setup instructions will be in the Domain settings page.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  onClick={handleSaveProfile}
                  disabled={updateProfileMutation.isLoading || !formData.domain}
                >
                  <Globe style={{ width: '1rem', height: '1rem' }} />
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save Custom Domain'}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        )
      },
      {
        id: 'complete',
        title: 'You\'re All Set!',
        description: 'Your account is ready to create amazing websites',
        icon: CheckCircle,
        isAction: false,
        content: (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ width: '5rem', height: '5rem', background: 'var(--success-soft)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <CheckCircle style={{ width: '2.5rem', height: '2.5rem', color: 'var(--success)' }} />
            </div>
            <h3 className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '1rem' }}>
              Congratulations!
            </h3>
            <p className="text-soft" style={{ marginBottom: '1.5rem', maxWidth: '28rem', margin: '0 auto 1.5rem' }}>
              Your account is now set up and ready to go. You can start creating your first website or explore the dashboard.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                className="btn btn-primary w-full"
                onClick={() => completeOnboardingMutation.mutate()}
                disabled={completeOnboardingMutation.isLoading}
              >
                {completeOnboardingMutation.isLoading ? 'Completing...' : 'Complete Setup'}
              </button>
              <button
                className="btn btn-ghost w-full"
                onClick={() => navigate('/client/sites/new')}
              >
                Create Your First Website
              </button>
            </div>
          </div>
        )
      }
    ]
  }

  const steps = getStepByStepFlow()
  const currentStepData = steps[currentStep]
  const completedRequiredSteps = steps.filter(step =>
    step.isAction && step.required && step.completed
  ).length
  const totalRequiredSteps = steps.filter(step => step.isAction && step.required).length

  useEffect(() => {
    if (checklist && completedRequiredSteps === totalRequiredSteps && currentStep < steps.length - 1) {
      setCurrentStep(steps.length - 1)
    }
  }, [completedRequiredSteps, totalRequiredSteps, currentStep, steps.length, checklist])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  if (!isOpen || isLoading || !checklist) {
    return null
  }

  if (checklist.onboardingCompleted) {
    return null
  }

  const WelcomeView = () => (
    <div style={{ padding: 32 }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ width: 72, height: 72, borderRadius: 20, margin: '0 auto 22px', background: 'linear-gradient(135deg, #A78BFA, #5B7CFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px -10px var(--accent-glow)' }}>
          <svg viewBox="0 0 24 24" width="38" height="38" fill="none"><path d="M8 8.5c1.6-2 6.4-2 8 0M8 12c1.6-2 6.4-2 8 0M8 15.5c1.6-2 6.4-2 8 0" stroke="white" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>Welcome to {cmsName}</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, marginTop: 12, maxWidth: 380, marginInline: 'auto' }}>
          Your workspace is ready. Let's get a few details so your site and brand are ready to go — it takes about a minute.
        </p>
      </div>

      {/* Progress Overview */}
      <div style={{ marginBottom: '2rem' }}>
        <div className="flex items-center justify-between" style={{ marginBottom: '0.5rem' }}>
          <span className="text-soft" style={{ fontSize: '0.875rem' }}>
            Setup Progress
          </span>
          <span className="font-medium" style={{ fontSize: '0.875rem', color: 'var(--text)' }}>
            {checklist.completedCount}/{checklist.totalCount}
          </span>
        </div>
        <div className="progress">
          <div
            className="progress"
            style={{ width: `${(checklist.completedCount / checklist.totalCount) * 100}%`, background: 'var(--accent)', height: '0.5rem' }}
          />
        </div>
      </div>

      {/* Checklist Items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
        {checklist.checklist.map((item) => (
          <div key={item.id} className="flex items-center justify-between" style={{ padding: '1rem', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
            <div className="flex items-center gap-3">
              {item.completed ? (
                <CheckCircle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--success)', flexShrink: 0 }} />
              ) : (
                <Circle style={{ width: '1.25rem', height: '1.25rem', color: 'var(--text-faint)', flexShrink: 0 }} />
              )}
              <div>
                <h4 className="font-medium" style={{ fontSize: '0.875rem', color: item.completed ? 'var(--text-muted)' : 'var(--text)', textDecoration: item.completed ? 'line-through' : 'none', margin: 0 }}>
                  {item.title}
                  {item.required && !item.completed && (
                    <span style={{ color: 'var(--danger)', marginLeft: '0.25rem' }}>*</span>
                  )}
                </h4>
                <p className="text-faint" style={{ fontSize: '0.75rem', marginTop: '0.125rem' }}>
                  {item.description}
                </p>
              </div>
            </div>
            {!item.completed && (
              <button
                onClick={() => {
                  setIsStepByStep(true)
                  const stepIndex = steps.findIndex(step => step.id === item.id)
                  if (stepIndex > 0) {
                    setCurrentStep(stepIndex)
                  }
                }}
                style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Start
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <button className="btn btn-primary w-full" onClick={() => setIsStepByStep(true)}>
          Start Step-by-Step Setup
        </button>

        {checklist.isComplete && (
          <button
            className="btn btn-primary w-full"
            style={{ background: 'var(--success)' }}
            onClick={() => completeOnboardingMutation.mutate()}
            disabled={completeOnboardingMutation.isLoading}
          >
            {completeOnboardingMutation.isLoading ? 'Completing...' : 'Complete Setup'}
          </button>
        )}

        <button className="btn btn-ghost w-full" onClick={handleSkipForNow}>
          Skip for Now
        </button>
      </div>
    </div>
  )

  const StepByStepView = () => (
    <div style={{ padding: '2rem' }}>
      {/* Step Progress */}
      <div style={{ marginBottom: '2rem' }}>
        <div className="flex items-center justify-between" style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => setIsStepByStep(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: 0 }}
          >
            <ArrowLeft style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
          <span className="text-faint" style={{ fontSize: '0.875rem' }}>
            Step {currentStep + 1} of {steps.length}
          </span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: 0 }}
          >
            <X style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {steps.map((_, index) => (
            <div
              key={index}
              style={{
                height: '0.5rem',
                borderRadius: '9999px',
                flex: 1,
                background: index <= currentStep ? 'var(--accent)' : 'var(--border)',
                transition: 'background 0.2s'
              }}
            />
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div style={{ minHeight: '25rem' }}>
        {currentStepData?.content}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between" style={{ paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
        <button
          className="btn btn-ghost"
          onClick={handlePrevious}
          disabled={currentStep === 0}
        >
          <ArrowLeft style={{ width: '1rem', height: '1rem' }} />
          Previous
        </button>

        <button
          className="btn btn-primary"
          onClick={handleNext}
          disabled={currentStep === steps.length - 1}
        >
          Next
          <ArrowRight style={{ width: '1rem', height: '1rem' }} />
        </button>
      </div>
    </div>
  )

  return (
    <div className="modal-backdrop">
      <div className="card" style={{ width: 540, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: 'var(--surface-2)' }}>
          <div style={{ height: '100%', width: `${((checklist.completedCount || 0) / Math.max(checklist.totalCount, 1)) * 100}%`, background: 'var(--accent)', transition: 'width 0.3s ease' }} />
        </div>
        {isStepByStep ? <StepByStepView /> : <WelcomeView />}
      </div>
    </div>
  )
}

export default OnboardingModal
